import type {
  HotelBookableOption,
  HotelBookableOptionAlternativeRoom,
  HotelBookableOptionWithAlternativeRoom,
  HotelBookableOptionWithPax,
  HotelBookableRoom,
  HotelIngredientAdapter,
  HotelIngredientResponse,
  Money,
  Pax,
  SimplifiedPax,
  RoomPax,
  UnavailableHotelIngredientResponse,
  HotelAlternativeOptionWithBasicRooms,
} from '@libs/trip-planner-interfaces';
import { IngredientType } from '@libs/trip-planner-interfaces';
import { assertValue, isDefined } from '@libs/utils';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

import type { IngredientResponseQL } from './itineraryResponseQL';
import { toMoney } from './moneyResponseQL';
import { toActivityIngredientResponse } from './activityIngredientResponseQL';
import {
  toHotelBookableOption,
  toHotelBookableRoom,
} from './hotelBookableOptionResponseQL';

export type HotelIngredientResponseQL = Extract<
  IngredientResponseQL,
  { type: 'HotelIngredientResponseQL' }
>;

const DEFAULT_MAX_HOTEL_SUGGESTIONS = 10;

export function toHotelIngredientResponse(
  hotelIngredientResponseQL: HotelIngredientResponseQL,
  hotelIngredientAdapter: HotelIngredientAdapter,
  maxNumberOfSuggestions = DEFAULT_MAX_HOTEL_SUGGESTIONS
): HotelIngredientResponse {
  const suggested = isDefined(hotelIngredientResponseQL.suggested)
    ? getSuggestedFromResponse(hotelIngredientResponseQL.suggested)
    : null;

  const unavailableHotelIngredientResponse =
    toUnavailableHotelIngredientResponse(
      hotelIngredientResponseQL,
      hotelIngredientAdapter,
      maxNumberOfSuggestions
    );

  if (suggested) {
    return {
      ...unavailableHotelIngredientResponse,
      isAvailable: true,
      suggested: suggested.withPax,
      suggestions: hotelIngredientResponseQL.suggestions
        ? hotelIngredientResponseQL.suggestions
            .slice(0, maxNumberOfSuggestions)
            .map(toHotelSuggestion)
        : [],
      options: [suggested.basic],
      addToBasketToken: suggested.basic.addToBasketToken,
      alternativeSuggestion: null,
    };
  } else {
    return unavailableHotelIngredientResponse;
  }
}

export type HotelIngredientResponseSuggestedQL = NonNullable<
  HotelIngredientResponseQL['suggested']
>;
function getSuggestedFromResponse(
  responseSuggested: HotelIngredientResponseSuggestedQL
): {
  basic: HotelBookableOption;
  withPax: HotelBookableOptionWithPax;
} {
  const hotelBookableOption = toHotelBookableOption(responseSuggested);

  assertSmartSearchList(responseSuggested.rooms);
  const rooms = responseSuggested.rooms.map(toHotelBookableRoom);

  return {
    basic: hotelBookableOption,
    withPax: {
      isPriceWithTaxes:
        isFeatureToggleActive('show-price-with-taxes') ||
        !!responseSuggested.prices?.priceIsWithTaxesIncluded,
      ...hotelBookableOption,
      pax: getTotalPax(rooms),
      rooms: rooms.map((roomObject) => ({
        description: roomObject.description,
        basicDescription: roomObject.basicDescription,
        cancellationPolicies: roomObject.cancellationPolicies,
        taxesAndFees: roomObject.taxesAndFees,
        price: roomObject.price,
        boardBasis: roomObject.boardBasis,
      })),
    },
  };
}

function getTotalPax(rooms: HotelBookableRoom[]): Pax {
  return rooms.reduce(
    (
      totalPax: Pax,
      room: HotelBookableOptionAlternativeRoom | HotelBookableRoom
    ) => {
      const roomPax: RoomPax = room.pax.reduce(
        (roomPax: RoomPax, currentRoomPax: SimplifiedPax) => {
          if (currentRoomPax.age === null) {
            roomPax.numberOfAdults++;
          } else {
            roomPax.childAges
              ? roomPax.childAges.push(currentRoomPax.age)
              : (roomPax.childAges = [currentRoomPax.age]);
          }
          return roomPax;
        },
        { numberOfAdults: 0 }
      );
      totalPax.push(roomPax);
      return totalPax;
    },
    []
  );
}

type SuggestionsQL = NonNullable<HotelIngredientResponseQL['suggestions']>;
type SuggestionQL = NonNullable<SuggestionsQL[number]>;
function toHotelSuggestion(
  suggestionQL: SuggestionQL
): HotelAlternativeOptionWithBasicRooms {
  assertSmartSearchValue(suggestionQL.prices?.deltaPrice);
  assertSmartSearchValue(suggestionQL.prices?.totalPrice);
  assertSmartSearchList(suggestionQL.rooms);
  assertSmartSearchValue(suggestionQL.addToBasketToken);
  assertSmartSearchValue(suggestionQL.code);

  const deltaPrice = toMoney(suggestionQL.prices.deltaPrice);
  const totalPrice = toMoney(suggestionQL.prices.totalPrice);
  return {
    addToBasketToken: suggestionQL.addToBasketToken,
    code: suggestionQL.code,
    isRefundable: suggestionQL.isRefundable,
    isPriceWithTaxes:
      isFeatureToggleActive('show-price-with-taxes') ||
      !!suggestionQL.prices.priceIsWithTaxesIncluded,
    prices: {
      deltaPrice,
      totalPrice,
      ...(suggestionQL.prices.grossPrice
        ? { grossPrice: toMoney(suggestionQL.prices.grossPrice) }
        : {}),
    },
    rooms: suggestionQL.rooms.map(toHotelBookableRoom),
    totalPrice,
  };
}

function toUnavailableHotelSuggestion(
  suggestionQL: SuggestionQL
): HotelAlternativeOptionWithBasicRooms | null {
  try {
    if (!suggestionQL.rooms) {
      return null;
    }
    assertSmartSearchList(suggestionQL.rooms);
    assertSmartSearchValue(suggestionQL.addToBasketToken);
    assertSmartSearchValue(suggestionQL.code);

    const rooms = suggestionQL.rooms.map(toHotelBookableRoom);
    if (rooms.length === 0) {
      return null;
    }

    const firstRoom = rooms[0];
    if (!firstRoom) {
      return null;
    }
    const roomPrice: Money = rooms.reduce(
      (acc, room) => ({
        amount: acc.amount + room.price.amount,
        currency: room.price.currency,
        currencySymbol: room.price.currencySymbol,
      }),
      {
        amount: 0,
        currency: firstRoom.price.currency,
        currencySymbol: firstRoom.price.currencySymbol,
      }
    );

    return {
      addToBasketToken: suggestionQL.addToBasketToken,
      code: suggestionQL.code,
      isRefundable: suggestionQL.isRefundable,
      isPriceWithTaxes:
        isFeatureToggleActive('show-price-with-taxes') ||
        !!suggestionQL.prices?.priceIsWithTaxesIncluded,
      prices: { deltaPrice: roomPrice, totalPrice: roomPrice },
      rooms,
      totalPrice: roomPrice,
    };
  } catch {
    return null;
  }
}

function toUnavailableHotelIngredientResponse(
  result: HotelIngredientResponseQL,
  ingredient: HotelIngredientAdapter,
  maxNumberOfSuggestions: number
): UnavailableHotelIngredientResponse {
  assertSmartSearchValue(result.date);
  assertSmartSearchValue(result.lengthOfStay);

  return {
    suggested: null,
    suggestions: result.suggestions
      ? result.suggestions
          .slice(0, maxNumberOfSuggestions)
          .flatMap(
            (suggestion) => toUnavailableHotelSuggestion(suggestion) ?? []
          )
      : [],
    options: [],
    addToBasketToken: null,
    date: new Date(result.date),
    numberOfNights: result.lengthOfStay.numberOfNights,
    isNumberOfNightsSuggested: result.lengthOfStay.nightsHaveBeenSuggested,
    ingredientType: IngredientType.Hotel,
    ingredientId: ingredient.ingredientId,
    subIngredientResponse:
      result.subIngredientResults?.map((subResult, subIndex) => {
        assertSmartSearchValue(subResult);

        const subIngredient = ingredient.subIngredients[subIndex];

        assertValue(
          subIngredient,
          'search-service-smart-search > mappers.ts > toHotelIngredientResponse > subIngredient'
        );

        if (subResult.type === 'TicketIngredientResponseQL') {
          return toActivityIngredientResponse(subResult, subIngredient);
        } else {
          throw new Error('Unhandled subIngredient type');
        }
      }) ?? [],
    isAvailable: false,
    alternativeSuggestion: result.alternativeSuggestion
      ? toAlternativeSuggestion(result.alternativeSuggestion)
      : null,
  };
}

type AlternativeSuggestionQL = NonNullable<
  HotelIngredientResponseQL['alternativeSuggestion']
>;
function toAlternativeSuggestion(
  alternativeSuggestionQL: AlternativeSuggestionQL
): HotelBookableOptionWithAlternativeRoom {
  assertSmartSearchList(alternativeSuggestionQL.rooms);
  assertSmartSearchValue(alternativeSuggestionQL.totalPrice);
  assertSmartSearchValue(alternativeSuggestionQL.addToBasketToken);
  assertSmartSearchValue(alternativeSuggestionQL.code);

  const rooms = alternativeSuggestionQL.rooms.map(
    toHotelBookableOptionAlternativeRoom
  );

  return {
    addToBasketToken: alternativeSuggestionQL.addToBasketToken,
    code: alternativeSuggestionQL.code,
    isRefundable: alternativeSuggestionQL.isRefundable,
    rooms,
    totalPrice: toMoney(alternativeSuggestionQL.totalPrice),
    pax: getTotalPax(rooms),
  };
}

type AlternativeSuggestionRoomsQL = NonNullable<
  AlternativeSuggestionQL['rooms']
>;
type AlternativeSuggestionRoomQL = NonNullable<
  AlternativeSuggestionRoomsQL[number]
>;
function toHotelBookableOptionAlternativeRoom(
  alternativeSuggestionRoomQL: AlternativeSuggestionRoomQL
): HotelBookableOptionAlternativeRoom {
  assertSmartSearchValue(alternativeSuggestionRoomQL.roomCode);

  return {
    ...toHotelBookableRoom(alternativeSuggestionRoomQL),
    roomCode: alternativeSuggestionRoomQL.roomCode,
  };
}
